import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { CapacitorFirebaseFirestoreService } from '@app/core/services/capacitor/capacitor-firebase/capacitor-firebase-firestore/capacitor-firebase-firestore.service';
import { FirebaseAuthenticationService } from '@app/core/services/firebase/firebase-authentication/firebase-authentication.service';
import { LoggingService } from '@app/core/services/logging/logging.service';

export const authGuard: CanActivateFn = async (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const firebaseAuthenticationService = inject(FirebaseAuthenticationService);
  const router = inject(Router);
  const firestoreService = inject(CapacitorFirebaseFirestoreService);

  const invite = new URLSearchParams(window.location.search).get('invite');
  if (invite) {
    localStorage.setItem(
      'bookingInvitation',
      JSON.stringify({
        bookingId: invite,
      }),
    );
  }

  let user = await firebaseAuthenticationService.getCurrentUser();

  // No user yet: silently sign-in as anonymous so the guest can keep
  // going (booking creation params in the URL are preserved).
  if (!user) {
    try {
      const result = await firebaseAuthenticationService.signInAnonymously();
      user =
        result.user || (await firebaseAuthenticationService.getCurrentUser());
    } catch (error) {
      LoggingService.error('[authGuard] Anonymous sign-in failed', error);
      return router.createUrlTree(['/login']);
    }
  }

  if (!user) {
    return router.createUrlTree(['/login']);
  }

  // Skip profile completion for anonymous users; they can upgrade later.
  if (user.isAnonymous) {
    return true;
  }

  const userDoc = await firestoreService.getDocument({
    reference: `users/${user.uid}`,
  });
  const userData = userDoc?.snapshot.data?.['profile'];
  const hasProfile = userData?.firstName || userData?.lastName;

  if (!hasProfile && !state.url.includes('/complete-profile')) {
    return router.createUrlTree(['/complete-profile']);
  }
  return true;
};
